/**
 * Currency helper — multi-tenant.
 *
 * The base prices in DB are stored in the company's *native* currency (EUR for IT, PLN for PL etc.)
 * The user can choose how to *display* prices:
 *  - 'native' (default): show in the company's native currency
 *  - 'eur'             : convert to EUR using exchange_rate_to_eur
 *
 * Exchange rates are set manually by the admin (no live FX) so prices remain stable.
 */

const SYMBOLS = {
  EUR: "€",
  PLN: "zł",
  USD: "$",
  GBP: "£",
};

/**
 * Format an amount using the active company's currency.
 * @param {number} amount    The numeric value (in the company's NATIVE currency, as stored in DB)
 * @param {object} company   Active company doc: { currency, currency_symbol, exchange_rate_to_eur }
 * @param {object} opts      { displayMode: 'native' | 'eur', decimals?: number }
 */
/**
 * Format a number in Italian locale (comma decimal, dot thousands).
 * Defaults: 2 decimal places. Used internally by formatMoney.
 */
const formatItalianNumber = (num, decimals = 2) => {
  const n = typeof num === "number" ? num : parseFloat(num || 0) || 0;
  return n.toLocaleString("it-IT", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};

export const formatMoney = (amount, company, opts = {}) => {
  const { displayMode = "native", decimals = 2 } = opts;
  const num = typeof amount === "number" ? amount : parseFloat(amount || 0) || 0;

  const currency = company?.currency || "EUR";
  const symbol = company?.currency_symbol || SYMBOLS[currency] || currency;
  const rate = parseFloat(company?.exchange_rate_to_eur ?? 1.0) || 1.0;

  if (displayMode === "eur" && currency !== "EUR" && rate > 0) {
    const eurValue = num / rate;
    return `${formatItalianNumber(eurValue, decimals)} €`;
  }
  return `${formatItalianNumber(num, decimals)} ${symbol}`;
};

/**
 * Returns just the symbol of the active company (€, zł, $, …).
 */
export const currencySymbol = (company) => {
  if (!company) return "€";
  return company.currency_symbol || SYMBOLS[company.currency] || company.currency || "€";
};

/**
 * Convert from company-native currency → EUR (or back) using the company's manual rate.
 * Useful for cross-tenant aggregated dashboards.
 */
export const toEur = (amount, company) => {
  const rate = parseFloat(company?.exchange_rate_to_eur ?? 1.0) || 1.0;
  return (parseFloat(amount) || 0) / rate;
};

export const fromEur = (amount, company) => {
  const rate = parseFloat(company?.exchange_rate_to_eur ?? 1.0) || 1.0;
  return (parseFloat(amount) || 0) * rate;
};
